import { FC } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import {
  tailwindOverride,
  Typography,
  IconButton,
  decimalHoursToTimeString,
} from '@getsynapse/design-system';
import { Hours } from 'types/store/actualHours';
import { selectCanDeleteActualHours } from 'state/ActualHours/actualHoursSlice';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface TimeEntryProps {
  hours: Hours;
  onDeleteHours: (hour: Hours) => void;
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

const TimeEntry: FC<TimeEntryProps> = ({ hours, onDeleteHours }) => {
  const canDelete = useSelector((state: RootState) =>
    selectCanDeleteActualHours(state, hours.id)
  );
  const { flexibleTaskTimeEntry } = useFlags();

  const hoursField = flexibleTaskTimeEntry
    ? decimalHoursToTimeString(hours.hours)
    : `${hours.hours}h`;

  return (
    <div
      className={tailwindOverride(
        'flex justify-between',
        'py-1 px-2 group',
        'hover:bg-off-white'
      )}
      aria-label={`time-entry-${hours.id}`}
    >
      <Typography variant='body'>
        {dateFormatter.format(new Date(hours.date))}
      </Typography>
      <div className='flex gap-x-4 items-center'>
        <Typography variant='body'>{hoursField}</Typography>
        <div className='w-8 h-full flex items-center'>
          {canDelete && (
            <IconButton
              name='trash'
              onClick={() => onDeleteHours(hours)}
              className={tailwindOverride(
                'hidden text-xl group-hover:flex text-neutral-dark'
              )}
              aria-label={`delete-time-entry`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeEntry;
